import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ArrowRight, Truck, CheckCircle2, Clock } from "lucide-react";
import api from "@/api/client";

const STATUS_META = {
  awaiting_payment: { label: "Awaiting Payment", color: "amber", icon: Clock },
  placed: { label: "Placed", color: "zinc", icon: Package },
  packed: { label: "Packed", color: "zinc", icon: Package },
  out_for_delivery: { label: "Out for Delivery", color: "orange", icon: Truck },
  delivered: { label: "Delivered", color: "green", icon: CheckCircle2 },
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));

  useEffect(() => {
    load().finally(() => setLoading(false));
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="border-b border-zinc-900 bg-white px-6 lg:px-10 py-10">
        <div className="tag">YOUR ORDERS</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mt-1">
          Live order tracker
        </h1>
        <p className="text-zinc-500 mt-2">
          {orders.length} order{orders.length === 1 ? "" : "s"} · auto-refreshing every 5s
        </p>
      </div>

      <div className="px-6 lg:px-10 py-10 space-y-3">
        {loading && (
          <div className="bg-white border border-zinc-300 p-8 text-center font-mono-data text-sm text-zinc-500">
            LOADING...
          </div>
        )}
        {!loading && orders.length === 0 && (
          <div className="bg-white border-2 border-dashed border-zinc-400 p-12 text-center">
            <Package className="w-8 h-8 mx-auto text-zinc-400" />
            <div className="font-display font-black text-2xl mt-4">No orders yet</div>
            <p className="text-zinc-500 mt-2">Once you order, your live tracker lives here.</p>
            <Link
              to="/catalog"
              data-testid="orders-empty-cta"
              className="mt-6 inline-block bg-[#FF5A00] text-white px-6 py-3 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm"
            >
              BROWSE CATALOG →
            </Link>
          </div>
        )}

        {orders.map((o) => {
          const s = STATUS_META[o.tracking.current_status] || STATUS_META.placed;
          const Icon = s.icon;
          return (
            <Link
              key={o.id}
              to={`/order/${o.id}`}
              data-testid={`order-row-${o.id.slice(0, 8)}`}
              className="block bg-white border border-zinc-900 hover:bg-zinc-50 transition"
            >
              <div className="p-5 flex flex-wrap items-center gap-4">
                <div className="w-14 h-14 bg-zinc-100 border border-zinc-900 flex items-center justify-center">
                  <Icon className="w-6 h-6" strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="tag">ORDER · {o.id.slice(0, 8).toUpperCase()}</div>
                  <div className="font-display font-bold text-lg leading-tight mt-1">
                    {o.items.length} item{o.items.length > 1 ? "s" : ""} ·{" "}
                    {o.items.slice(0, 2).map((i) => i.name).join(", ")}
                    {o.items.length > 2 ? "..." : ""}
                  </div>
                  <div className="text-xs font-mono-data text-zinc-500 mt-1">
                    Placed {new Date(o.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="text-right">
                  <div className="tag">STATUS</div>
                  <div
                    className={`inline-block mt-1 px-2 py-1 border font-mono-data text-xs tracking-widest ${
                      s.color === "green"
                        ? "border-green-600 text-green-700 bg-green-50"
                        : s.color === "orange"
                        ? "border-[#FF5A00] text-[#FF5A00] bg-orange-50"
                        : s.color === "amber"
                        ? "border-amber-500 text-amber-700 bg-amber-50"
                        : "border-zinc-900 text-zinc-900 bg-zinc-100"
                    }`}
                  >
                    {s.label.toUpperCase()}
                  </div>
                  <div className="text-xs font-mono-data text-zinc-500 mt-1">
                    ETA {o.tracking.eta_display}
                  </div>
                </div>

                <div className="text-right border-l border-zinc-200 pl-4">
                  <div className="tag">TOTAL</div>
                  <div className="font-mono-data font-bold text-lg mt-1">
                    ₹{o.total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </div>
                </div>

                <ArrowRight className="w-5 h-5" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
